import { SizableText, YStack } from '@mythical/ui'
import { Meta } from 'app/components/primitive/Meta'
import PerksPanel from 'app/components/primitive/PerksPanel'
import Spinner from 'app/components/primitive/Spinner'
import { Container } from 'app/components/structure/Container'
import MainLayout from 'app/components/structure/Layout/MainLayout'
import Section from 'app/components/structure/Section'
import { isValidPlanLevel, PlanLevel } from 'app/constants/tiers'
import useQuery from 'app/hooks/use-query'
import { createPerksQuery, mapPerks } from 'app/queries/perks/perks'
import {
  createTiersQuery,
  Frequency,
  mapToTiers,
} from 'app/queries/tiers/member-tiers'
import { useStores } from 'app/store'
import { observer } from 'mobx-react-lite'
import { createParam } from 'solito'

const tiersQuery = createTiersQuery()
const perksQuery = createPerksQuery()
const { useParam } = createParam<{ tier?: string; frequency?: string }>()

const params = {}
const LEGACY_ALL_ACCESS_TIER_LEVEL = 3

const parseTierParam = (tier?: string | null): PlanLevel | undefined => {
  if (!tier) return undefined
  const parsedTier = Number(tier)
  if (Number.isNaN(parsedTier)) return undefined
  if (parsedTier === LEGACY_ALL_ACCESS_TIER_LEVEL) return 2
  if (!isValidPlanLevel(parsedTier)) return undefined
  return parsedTier
}

const parseFrequencyParam = (
  frequency?: string | null,
): Frequency | undefined => {
  if (
    frequency !== 'monthly' &&
    frequency !== 'quarterly' &&
    frequency !== 'annually'
  ) {
    return undefined
  }
  return frequency
}

export const PerksScreen = observer(() => {
  const { userStore } = useStores()
  const [tierParam] = useParam('tier')
  const [frequencyParam] = useParam('frequency')
  const canMemberfulLink = userStore?.canMemberfulLink ?? false
  const initialTierLevel = parseTierParam(tierParam)
  const initialFrequency = parseFrequencyParam(frequencyParam)
  const { data, loading: tiersLoading } = useQuery(
    tiersQuery,
    params,
    mapToTiers,
  )
  const { data: perksData, loading: perksLoading } = useQuery(
    perksQuery,
    params,
    mapPerks,
  )

  const loading = tiersLoading || perksLoading

  return (
    <MainLayout noSidebar noFooter={false} noSearch showBackBtn>
      <Meta title="Perks" slug="perks" />

      <Section paddedX bg="$bgPrimary">
        <Container variant="mediumLarge">
          {!canMemberfulLink && (
            <YStack py="$3.5" w="100%" jc="center" ai="center">
              <SizableText size="$m" color="$fgSecondary" ta="center">
                Membership upgrades are not available in this region.
              </SizableText>
            </YStack>
          )}
          {loading && (
            <YStack py="$3.5" w="100%" jc="center" ai="center">
              <Spinner size="large" />
            </YStack>
          )}
          {!loading && canMemberfulLink && (
            <PerksPanel
              perks={perksData}
              memberTiers={data}
              initialTierLevel={initialTierLevel}
              initialFrequency={initialFrequency}
            />
          )}
        </Container>
      </Section>
    </MainLayout>
  )
})
